// How lively the companions are: how long they rest between the things they do, how often they
// fidget while resting (look around, lounge, do something of their own), and how often fights,
// parties and games happen. The owner picks a level for the whole screen and can give a companion
// a level of its own (settings.js; "like everyone" by default). What a companion paces by itself
// (its rest, its little moves, its own things) follows its own level; what concerns the whole
// screen (fights, parties and games, and the get-togethers abilities start) follows the screen's.
// "normal" is how they've always been. At "background" they mostly sit and chill and only do
// something every few minutes.

export const ACTIVITY = {
  lively:     { label: 'Lively',     rest: 0.55, fidget: 0.8, own: 0.16, fights: 0.7, together: 0.75 },
  normal:     { label: 'Normal',     rest: 1,    fidget: 1,   own: 0.12, fights: 1,   together: 1 },
  calm:       { label: 'Calm',       rest: 3,    fidget: 1.5, own: 0.07, fights: 2,   together: 2 },
  chill:      { label: 'Chill',      rest: 8,    fidget: 2.5, own: 0.04, fights: 4,   together: 5 },
  background: { label: 'Background', rest: 25,   fidget: 4,   own: 0.02, fights: 10,  together: 15 },
};
// rest: how much longer it rests between things. fidget: how much longer between little moves
// while resting. own: the chance that a little move is one of its own things (a sheep grazing).
// fights: how much longer between fights and evil spells (fights.js: about once in 30 to 60
// minutes, and at most once in two hours, at normal). together: how much longer between parties
// and games (the runtime's director: one about every 6 to 10 minutes at normal) and between the
// get-togethers abilities start (gatherings() below); about as much longer as a companion's own
// things get at that level, so they stay a little rarer than those.

// A host can make parties and games rarer still, or a little more frequent (the website's
// data-gatherings): how much longer they wait (never: they don't happen on their own). The menu
// doesn't offer it: parties and games are switches in each companion's actions.
export const GATHERINGS = {
  often:     { label: 'Often',     wait: 0.5 },
  normal:    { label: 'Normal',    wait: 1 },
  sometimes: { label: 'Sometimes', wait: 3 },
  rarely:    { label: 'Rarely',    wait: 8 },
  never:     { label: 'Never',     wait: Infinity },
};

// A companion's own levels: the screen's, and after Background its last one, Still (the owner,
// 2026-11-01): it stays where it is and takes part in nothing. It keeps its drawing's own life
// (it blinks, looks about, breathes), can be picked up and clicked, and finds a new spot when its
// spot goes away, as everyone does while Alive is off; but none of its own behavior runs, and it
// is never free for anything others start (parties, games, fights, evil spells, helping against
// pests, the visits and meetings abilities start). A Show me still plays on it (runtime/showme.js).
// Only a companion has it (settings.js): the screen's levels stay Lively to Background, so a
// host's or saved screen level 'still' is refused like any unknown one. (Not the website's Still,
// web/still.js, which is a visitor's pause for everyone.)
export const STILL = 'still';
export const OWN_LEVELS = { ...ACTIVITY, [STILL]: { label: 'Still' } };
export const keepsStill = me => me?.activity === STILL;

// The levels in use. Whoever runs the companions (the preview, AresOS) sets them.
export const pace = { activity: 'normal', gatherings: 'normal' };
const levelOf = (levels, key) => (Object.hasOwn(levels, key) ? levels[key] : null);
// The screen's level, for everything that concerns the whole screen.
export const activity = () => levelOf(ACTIVITY, pace.activity) || ACTIVITY.normal;
// A companion's own level (me.activity), or the screen's when it has none: for what it paces by itself.
export const activityOf = me => levelOf(ACTIVITY, me?.activity) || activity();
// How much longer the whole screen waits between get-togethers (parties, games, and the meetings,
// visits and rides abilities start with others): the screen's level and the host's choice. One
// pace for everyone, however many companions could start one.
export const gatherings = () => ({ wait: activity().together * (levelOf(GATHERINGS, pace.gatherings) || GATHERINGS.normal).wait });
